import TenantComponents from '@components';
import tenantConstants from '@constants';
import tenantData from '@data';
import tenantTheme from '@theme';
import tenantUtils from '@utils';
import { Badge, Divider, Space, Typography, Tooltip } from 'antd';
import { t } from 'i18next';
import React, { useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getTimeDateString } from '../../../utility/date';
import { getBaseURL, TENANT_KEY } from '../../../utility/env';
import { capitalizeFirstLetter, hasBedsOrBathsValue } from '../../../utility/utility';
import { Flex, Icon, Image, LinkWithIcon, Number as NumberDisplay, Popover, ProductTag, Tag, TextWithIcon } from '../../common';
import DiscountTag from '../../discount-tag/DiscountTag';
import Group from '../../common/group/group';
import ListingDrawer from '../../listing-drawer/listingDrawer';
import { Thumbnail } from '../../styled';
import { IconSwitch } from '../../svg';
import dayjs from 'dayjs';
import {
  normalizeBookedDateRanges,
  hasBookedRangeIncludingToday,
  getActiveBookingEndDateString,
} from '../../../helpers/bookedDateRanges';
import { isUnifiedProfolioEnabled } from '../../../utility/user';
import { getAppliedListingProductSlugFromProductsInfo, getMergedListingProductsInfo } from '../../../tenant/common/transformers/listings';
import { getUnifiedListingTierProductTagProps } from '../../../tenant/common/utils/unifiedListingTierBadge';
const { Text } = Typography;

const AREA_UNIT_SQ_M_EN = 'Sq. M.';
const AREA_UNIT_SQ_M_AR = 'م²';

export const ListingPurpose = (props) => {
  const {
    productsInfo = {},
    hideHealthPopover,
    disableDetailDrawer = false,
    isOffPlan,
    projectStatus,
    saleType,
    showExternalLink = false,
    booked,
    bookedDates,
    discount_percentage,
    discount_applied,
    price,
    actual_price,
  } = props;
  const isProject = !!props?.project_info?.id;
  // Purpose-agnostic: the discount tag renders for any listing the backend flags as discounted
  // (sale or rent), keyed on the same fields — matching the listing card and the post-listing form.
  const showDiscountTag =
    tenantConstants.SHOW_LISTING_DISCOUNT_TAG &&
    discount_applied === true &&
    discount_percentage != null &&
    discount_percentage !== '';

  const discountTagActualPriceDisplay = actual_price?.value ?? actual_price ?? price?.value;

  const listingDrawerRef = useRef();
  const { i18n } = useTranslation();
  const listingAreaUnitShort = useMemo(() => {
    const lang = (i18n.language || '').toLowerCase();
    return lang.startsWith('ar') ? AREA_UNIT_SQ_M_AR : AREA_UNIT_SQ_M_EN;
  }, [i18n.language]);
  // A listing with no area size renders no area chip at all. Interpolating the raw value
  // instead printed `undefined Sq. M.` whenever the payload carried no size.
  // Tenants with selectable area units pass the listing's own unit as `area.unitLabel`.
  const areaSizeLabel = useMemo(() => {
    const value = props?.area?.value;
    const unitLabel = props?.area?.unitLabel ? t(props.area.unitLabel) : listingAreaUnitShort;
    return value == null || value === '' ? null : `${value} ${unitLabel}`;
  }, [props?.area?.value, props?.area?.unitLabel, listingAreaUnitShort]);
  const rtl = useSelector((state) => state.app.AppConfig.rtl);
  const isMobile = useSelector((state) => state.app.AppConfig.isMobile);
  const statusBasedTag = tenantUtils?.getStatusBasedTagProps && tenantUtils.getStatusBasedTagProps(props?.status);
  const normalizedBookedDates = useMemo(() => normalizeBookedDateRanges(bookedDates), [bookedDates]);
  const today = dayjs().startOf('day');
  const user = useSelector((state) => state.app.loginUser.user);
  const isUnifiedPostListing = isUnifiedProfolioEnabled({ featureKey: 'postListing' });
  const isUnifiedListings = isUnifiedProfolioEnabled({ featureKey: 'listingsUnified' });
  const mergedProductsInfo = useMemo(() => {
    if (productsInfo && Object.keys(productsInfo).length > 0) return productsInfo;
    return getMergedListingProductsInfo(props?.platforms != null ? props : { property: props });
  }, [productsInfo, props]);
  const unifiedListingTypeChip = useMemo(() => {
    if (!isUnifiedListings) return null;
    const slug = getAppliedListingProductSlugFromProductsInfo(mergedProductsInfo);
    if (!slug) return null;
    const tagProps = tenantData.getListingActions(slug);
    if (!tagProps) return null;
    return getUnifiedListingTierProductTagProps(slug, tagProps);
  }, [isUnifiedListings, mergedProductsInfo]);

  const nonUnifiedProductTagProps = useMemo(() => {
    if (isUnifiedListings || !props?.isBadge) return null;
    const fromGetTagProps = tenantData.getTagProps(mergedProductsInfo);
    if (fromGetTagProps) return fromGetTagProps;
    const slug = getAppliedListingProductSlugFromProductsInfo(mergedProductsInfo);
    if (!slug) return null;
    return tenantData.getListingActions(slug);
  }, [isUnifiedListings, props?.isBadge, mergedProductsInfo]);
  const imageColumnTemplate = isUnifiedListings ? '135px 1fr' : '106px 1fr';
  const badgeImageCountOffset = isUnifiedListings ? [-135, 0] : [-105, 0];
  const bookingEndDate = useMemo(() => {
    const activeEnd = getActiveBookingEndDateString(normalizedBookedDates);
    if (activeEnd) return activeEnd;
    if (!normalizedBookedDates.length) return null;
    const ends = normalizedBookedDates.map((r) => dayjs(r.end_date)).filter((d) => d.isValid());
    return ends.length ? ends.reduce((a, b) => (a.isAfter(b) ? a : b)).format('YYYY-MM-DD') : null;
  }, [normalizedBookedDates]);
  const hasActiveBookingRange = useMemo(
    () => hasBookedRangeIncludingToday(normalizedBookedDates),
    [normalizedBookedDates],
  );
  const showBookedChip = Boolean(booked) || hasActiveBookingRange || normalizedBookedDates.length > 0;
  const handleClick = (e) => {
    e.stopPropagation();
    !disableDetailDrawer && listingDrawerRef?.current && listingDrawerRef.current.open(props?.id);
  };
  const rawListingPurposeTitle =
    tenantUtils.getLocalisedString(props?.purpose, 'title') || props?.purpose?.title;
  const lang = (i18n.language || '').toLowerCase();
  const isEn = lang === 'en' || lang.startsWith('en-');
  const listingPurposeTitle =
    isEn && String(rawListingPurposeTitle || '').trim().toLowerCase() === 'daily-rental'
      ? 'Daily Rental'
      : rawListingPurposeTitle;
  const totalImages = props?.imageCount ?? props?.images?.length ?? 0;
  const showUnifiedImageCountBadge = (isUnifiedPostListing || isUnifiedListings) && totalImages > 0;

  const renderUnifiedImageCountBadge = () => {
    if (!showUnifiedImageCountBadge) return null;
    return (
      <div
        style={{
          position: 'absolute',
          insetInlineStart: 8,
          bottom: 8,
          zIndex: 2,
          background: '#000000B2',
          color: '#fff',
          borderRadius: 12,
          padding: '2px 8px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          fontSize: 11,
          fontWeight: 600,
          lineHeight: 1.2,
        }}
      >
        <Icon icon="HiCamera" size="12px" color="#fff" />
        <span>{totalImages}</span>
      </div>
    );
  };

  /**
   * Project unit-type listings (Bayut KSA off-plan / ready inventory) surface the project's
   * off-plan state inside the purpose tag, and the project's WAFI licence in place of the REGA ID.
   * Both stay null on every other listing and tenant, so those rows render unchanged.
   */
  const isUnitTypeListing = props?.details?.isUnitTypeListing === true;
  const unitTypeStatusLabel = isUnitTypeListing
    ? props?.details?.unitTypeProjectStatus === 'ready'
      ? t('Ready')
      : props?.details?.unitTypeProjectStatus === 'off_plan'
        ? t('Off-plan')
        : null
    : null;

  const locationBreadcrumb = isProject
    ? props?.location?.breadcrumbs
        ?.filter((e) => e?.level > 1)
        ?.map((e) => tenantUtils.getLocalisedString(e, 'title'))
        ?.join(', ')
    : props?.location?.breadcrumb;

  if (isProject) {
    return (
      <Group
        template="106px 1fr"
        gap=".571rem"
        onClick={(e) => e.stopPropagation()}
        style={{ marginBottom: '14px' }}
      >
        <Flex align="start" gap=".571rem">
          <Badge
            count={showUnifiedImageCountBadge ? null : props?.imageCount}
            size="default"
            overflowCount={90}
            offset={!rtl && [-105, 0]}
            style={{ backgroundColor: '#00a651' }}
            shape="square"
            onClick={(e) => e.stopPropagation()}
          >
            <Thumbnail style={{ position: 'relative' }}>
              {renderUnifiedImageCountBadge()}
              {showBookedChip && (
                <Tooltip
                  title={
                    <TextWithIcon icon="InfoIcon" value={`${t('Booked Until')} ${getTimeDateString(bookingEndDate)}`} />
                  }
                >
                  <div
                    style={{
                      marginBottom: '4px',
                    }}
                  >
                    <ProductTag icon="BookedBadge" name={t('Booked')} tagBackgroundColor="#00A174" />
                  </div>
                </Tooltip>
              )}

              <Image
                onClick={handleClick}
                style={{ cursor: 'pointer' }}
                src={props?.images?.[0]?.sizes?.thumbnail}
                fallback={`${getBaseURL()}/profolio-assets/images/ph-listings.svg`}
              />
              {showDiscountTag && (
                <div
                  style={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    zIndex: 2,
                    lineHeight: 0,
                  }}
                >
                  <DiscountTag discount_percentage={discount_percentage} actualPriceDisplay={discountTagActualPriceDisplay} />
                </div>
              )}
            </Thumbnail>
          </Badge>
        </Flex>

        <Group className="fz-12 py-2" template="initial" gap="4px">
          <Space className="fz-14 fw-700" style={{ lineHeight: 1.2 }}>
            <span className="text-primary">
              {tenantUtils.getLocalisedString(props, 'title')}
            </span>
          </Space>
          <div>
            <TextWithIcon
              icon="Location"
              iconProps={{ size: '1.4em', color: tenantTheme.gray500 }}
              textColor={tenantTheme.gray700}
              value={locationBreadcrumb}
              style={{ maxWidth: '30ch' }}
            />
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'space-between',
            }}
          >
            {props?.total_units && (
              <TextWithIcon
                icon="AreaUnitIcon"
                iconProps={{ size: '1.4em', color: tenantTheme.gray500 }}
                textColor={tenantTheme.gray700}
                value={`${props.total_units} ${t('Units')}`}
              />
            )}
            {props?.under_construction ? (
              <ProductTag name={t('Under Construction')} tagBackgroundColor="rgba(131, 60, 138, 1)" />
            ) : (
              <ProductTag name={t('Ready')} tagBackgroundColor="#00a651" />
            )}
          </div>
        </Group>
        <ListingDrawer ref={listingDrawerRef} listingId={disableDetailDrawer ? undefined : props?.id} />
      </Group>
    );
  }

  return (
    <Group
      template={imageColumnTemplate}
      gap=".571rem"
      onClick={(e) => e.stopPropagation()}
    >
      <Group template={imageColumnTemplate} gap="5rem">
        <Badge
          count={showUnifiedImageCountBadge ? null : props?.imageCount}
          size="default"
          overflowCount={90}
          offset={!rtl && badgeImageCountOffset}
          style={{ backgroundColor: '#00a651' }}
          shape="square"
          onClick={(e) => e.stopPropagation()}
        >
          {/* antd's Badge wraps at fit-content, so a thumbnail smaller than the column (e.g. 84x84) rendered at
              its own size; zameen keeps it at the column's 106x106. */}
          <Thumbnail style={{ position: 'relative', ...(TENANT_KEY === 'zameen' && { minWidth: 106, minHeight: 106 }) }}>
            {renderUnifiedImageCountBadge()}
            {showBookedChip && (
              <Tooltip
                title={
                  <TextWithIcon icon="InfoIcon" value={`${t('Booked Until')} ${getTimeDateString(bookingEndDate)}`} />
                }
              >
                <div
                  style={{
                    position: 'absolute',
                    top: 6,
                    insetInlineStart: 10,
                    zIndex: 2,
                  }}
                >
                  <ProductTag icon="BookedBadge" name={t('Booked')} tagBackgroundColor="#00A174" />
                </div>
              </Tooltip>
            )}

            <Image
              onClick={handleClick}
              style={{ cursor: 'pointer' }}
              src={typeof props?.image === 'string' ? props.image : props?.image?.thumbnail}
              fallback={`${getBaseURL()}/profolio-assets/images/ph-listings.svg`}
            />
            {showDiscountTag && (
              <div
                style={{
                  position: 'absolute',
                  top: 6,
                  right: 6,
                  zIndex: 2,
                  lineHeight: 0,
                }}
              >
                <DiscountTag discount_percentage={discount_percentage} actualPriceDisplay={discountTagActualPriceDisplay} />
              </div>
            )}
          </Thumbnail>
        </Badge>
      </Group>

      <Group className="fz-12 py-2" template="initial" gap="4px">
        <Space className="fz-14 fw-700" style={{ lineHeight: 1.2 }}>
          <NumberDisplay className="text-primary" compact={false} type="price" {...price} />
          {props?.isDailyRental && (
            <span className="fw-500" style={{ color: tenantTheme['primary-text-color'] }}>
              {t('Night')}
            </span>
          )}
          {isUnifiedListings && unifiedListingTypeChip && !isMobile && (
            <ProductTag {...unifiedListingTypeChip} />
          )}
          {!isUnifiedListings && nonUnifiedProductTagProps && (
            <ProductTag
              {...nonUnifiedProductTagProps}
              iconProps={{ ...(nonUnifiedProductTagProps.iconProps || {}), size: '1.2em' }}
            />
          )}
          {statusBasedTag && (
            <ProductTag
              {...statusBasedTag}
              iconProps={{ ...statusBasedTag?.iconProps, size: '10px', color: '#222' }}
              tagStyle={{ opacity: '0.5' }}
            />
          )}
          {showExternalLink && props?.url && (
            <LinkWithIcon as="a" icon={<IconSwitch />} href={props?.url} className="btnLink px-8" target="_blank" rel="noopener noreferrer" />
          )}
          {!hideHealthPopover && props.health && <TenantComponents.Health {...props.health} listingsData={props} />}
        </Space>
        <span>
          {props?.type?.title && listingPurposeTitle ? (
            <Tag
              color={tenantTheme['primary-light-4']}
              style={{ '--tag-color': tenantTheme['primary-text-color'], fontWeight: '700' }}
            >
              {' '}
              {unitTypeStatusLabel ? (
                <>
                  {`${tenantUtils.getLocalisedString(props?.type, 'title')} ${t('for')} ${listingPurposeTitle}`}
                  <span style={{ opacity: 0.4, fontWeight: 400, paddingInline: 4 }}>|</span>
                  {unitTypeStatusLabel}{' '}
                </>
              ) : isOffPlan
                ? `${tenantUtils.getLocalisedString(props?.type, 'title')} | ${t(projectStatus)}  ${projectStatus != 'Ready' ? ' | ' + tenantUtils.getLocalisedString(saleType, 'label') : ''}`
                : `${tenantUtils.getLocalisedString(props?.type, 'title')} ${t('for')} ${listingPurposeTitle}${
                    tenantConstants.SHOW_COMPLETION_STATUS_FOR_SELL &&
                    props?.purpose?.slug === 'sale' &&
                    !!props?.completion_status
                      ? ` | ${t(projectStatus)}`
                      : ' '
                  }`}
            </Tag>
          ) : isUnifiedListings && props?.listingTitle ? (
            <Tag
              color={tenantTheme['primary-light-4']}
              style={{ '--tag-color': tenantTheme['primary-text-color'], fontWeight: '700' }}
            >
              {props.listingTitle}
            </Tag>
          ) : null}
        </span>
        <Space wrap={true} gap="10px">
          {props?.details?.listingSpecs
            ?.filter((data) => hasBedsOrBathsValue(data?.value) && !tenantData.shouldHideBedsBathsForListing?.(props))
            ?.map((e, index) => (
              <TextWithIcon
                key={index}
                icon={e?.icon}
                iconProps={e?.iconProps}
                textColor={e?.textColor}
                value={e?.value}
              />
            ))}
          {areaSizeLabel && (
            <TextWithIcon
              icon="IconAreaSize"
              iconProps={{ size: '1.4em', color: tenantTheme.gray500 }}
              textColor={tenantTheme.gray700}
              value={areaSizeLabel}
            />
          )}
        </Space>
        <div>
          <div style={{ maxWidth: '30ch' }}>{locationBreadcrumb}</div>
        </div>
        <span className="fz-10">
          <Text className="text-muted fz-10">
            {isUnifiedPostListing || isUnifiedListings
              ? t('Listing ID')
              : tenantConstants.SHOW_REGA_DETAIL
                ? t('Bayut ID')
                : t('ID')}
          </Text>{' '}
          <Text className="text-primary fz-10">{props?.id}</Text>
          {(isUnifiedPostListing || isUnifiedListings) && (
            <span style={{ display: 'block', marginTop: 4 }}>
              <Text className="text-muted fz-10">{t('Assigned To')}</Text>{' '}
              <Text className="text-primary fz-10">{props?.assignedTo || props?.assigned_to || '—'}</Text>
            </span>
          )}
          {tenantConstants.SHOW_REGA_DETAIL && (
            <>
              {isUnitTypeListing ? (
                // Unit-type listings carry the project's WAFI licence instead of a per-ad REGA
                // licence. A missing licence is expected here, so no `Unlicensed` fallback.
                !!props?.details?.wafiLicense && (
                  <>
                    <Divider type="vertical" />
                    <Flex wrap={false} gap="2px" style={{ display: 'inline-flex' }}>
                      <Text className="text-muted fz-10">{t('WAFI Licence:')} </Text>
                      <Text className="text-primary inline-flex align-items-center fz-10" style={{ gap: 2 }}>
                        {props?.details?.wafiLicense}
                      </Text>
                    </Flex>
                  </>
                )
              ) : props?.details?.regaId ? (
                <>
                  <Divider type="vertical" />
                  <Flex wrap={false} gap="2px" style={{ display: 'inline-flex' }}>
                    <Text className="text-muted fz-10">{t('REGA ID:')} </Text>
                    <Text className="text-primary inline-flex align-items-center fz-10" style={{ gap: 2 }}>
                      {props?.details?.regaId}
                      {props?.details?.regaExpiryDate && (
                        <Popover
                          content={
                            <>
                              {t('Expiring on')}: <strong>{getTimeDateString(props?.details?.regaExpiryDate)}</strong>
                            </>
                          }
                          action="hover"
                          getPopupContainer={() => document.body}
                        >
                          <>
                            <Icon icon="AiOutlineInfoCircle" color={tenantTheme.gray700} />
                          </>
                        </Popover>
                      )}
                    </Text>
                  </Flex>
                </>
              ) : props?.details?.permit_number ? (
                <>
                  <Divider type="vertical" style={{ marginInline: 4 }} />
                  <Text className="text-muted" style={{ fontSize: 10 }}>
                    {t('Permit No:')}{' '}
                  </Text>
                  <Text className="text-primary" style={{ fontSize: 10 }}>
                    {props?.details?.permit_number}
                  </Text>
                </>
              ) : (
                <>
                  <Divider type="vertical" />
                  <Tag shape="round" style={{ alignSelf: 'center', fontSize: 12, lineHeight: 'normal' }}>
                    {t('Unlicensed')}
                  </Tag>
                </>
              )}
            </>
          )}
        </span>
      </Group>
      <ListingDrawer ref={listingDrawerRef} listingId={disableDetailDrawer ? undefined : props?.id} />
    </Group>
  );
};
